//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { patchApplication } from '@/api/Rover/ApplicationForm';
import type { IPatchApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import { addApplicationTeamMember } from '@/api/Rover/applicationTeam';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
//Custom components
import { HookFormProvider, type HookFormProviderProps } from '@/components/UI/HookForm/Provider';
import Modal, { type ModalProps } from '@/components/UI/Modal';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '@/store/Toast';
import type { TitleInterface } from '@/types/Account';
import type { UserOption } from '@/types/Constants';
import type { User } from '@/types/User';
import { sanitizeResponseError } from '@/utils';

//Component Begin

interface IAddProjectTeamMemberFormData {
  member: { id: number; name: string; email: string };
}

interface IAddRoverTeamMemberFormProps extends ModalProps {
  hookFormConfig?: HookFormProviderProps;
  title: string;
  users?: User[];
  modalHandler: () => void;
  memberType: 'LEAD' | 'MEMBER';
}

const RoverTeamMemberFormBase = ({ hookFormConfig, title, users, modalHandler, memberType, ...rest }: IAddRoverTeamMemberFormProps) => {
  const { applicationId } = useParams<string>();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const accountTitles = useOrgTitles({});
  const titles = accountTitles.data?.data;

  const memberForm = useFormContext();
  const { watch } = memberForm;
  const [orgTitle] = watch(['title']);

  const addRoverTeamMember = useMutation(
    (data: IPostApplicationTeamMemberPayload) =>
      addApplicationTeamMember({
        data,
        applicationId: applicationId,
      }),
    {
      onSuccess: () => {
        modalHandler();
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS_TEAM_MEMBERS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Application member added successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const assignApplicationLead = useMutation(
    (data: Partial<IPatchApplicationServicePayload>) =>
      patchApplication({
        data,
        applicationId: applicationId!,
      }),
    {
      onSuccess: () => {
        modalHandler();
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS_TEAM_MEMBERS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Application lead assigned successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: IAddProjectTeamMemberFormData) => {
    if (memberType === 'MEMBER') {
      addRoverTeamMember.mutate({ userId: formData.member.id });
    } else {
      assignApplicationLead.mutate({ leadId: formData.member.id });
    }
  };

  return (
    <Modal {...rest}>
      <h2 className='mt-0'>{`${title}`}</h2>
      <HookForm onSubmit={onSubmit}>
        <div className='space-y-4'>
          <HookReactSingleSelect
            id='title'
            options={titles}
            getOptionLabel={(option) => (option as TitleInterface).name}
            getOptionValue={(option) => (option as TitleInterface).roleId}
            placeholder='Select Title'
            name='title'
            label='Organizational Title'
            isClearable
          />
          <HookReactSingleSelect
            id='member'
            options={orgTitle ? users?.filter((user) => user.title === orgTitle?.name) : users}
            getOptionLabel={(option) => (option as UserOption).name}
            getOptionValue={(option) => (option as UserOption).id.toString()}
            placeholder='Select'
            name='member'
            label='Member'
            required
          />
          <div className='flex justify-end gap-4'>
            <Button disabled={addRoverTeamMember.isLoading} label='Cancel' type='button' btnStyle='btn-neutral' onClick={modalHandler} />
            <Button
              disabled={addRoverTeamMember.isLoading}
              label={addRoverTeamMember.isLoading ? 'Submitting...' : 'Submit'}
              type='submit'
              btnStyle='btn-primary'
            />
          </div>
        </div>
      </HookForm>
    </Modal>
  );
};

export const AddRoverTeamMemberForm = ({ hookFormConfig, ...rest }: IAddRoverTeamMemberFormProps) => (
  <HookFormProvider>
    <RoverTeamMemberFormBase {...rest} />
  </HookFormProvider>
);
